import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "../api/client";

/* ── identifiers ─────────────────────────────────────────────────────────────────────── */

/** Guids are the only identity this store has, so they are copyable everywhere. */
/** Head plus random tail, for a filter chip where a full guid would not fit. */
export function shorten(value: string | null | undefined): string {
  if (!value) return "—";
  return value.length > 14 ? `${value.slice(0, 8)}…${value.slice(-6)}` : value;
}

/**
 * A guid renders in full. It is the only identity this store has, so an operator reading a
 * ticket needs to compare it character by character — and uuid7 puts a timestamp in its
 * leading bits, so a left-truncated list of same-day guids reads as one repeated value.
 * `short` exists for the few slots that are genuinely narrow (a filter chip), and keeps the
 * random tail for exactly that reason.
 */
export function Guid({
  value,
  to,
  short = false,
}: {
  value: string | null | undefined;
  to?: string;
  short?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  if (!value) return <span className="mono text-faint">—</span>;

  const text = short ? shorten(value) : value;
  // A guid is routinely rendered inside a clickable row, so its own link must not also
  // trigger the row's navigation.
  // The design system's link rule: body-text color with a Silver500 underline, turning
  // Blue500 only on hover — not blue at rest. A screen of blue guids is not the DS pattern.
  const body = to ? (
    <Link
      to={to}
      onClick={(event) => event.stopPropagation()}
      className="group/guid mono select-all whitespace-nowrap underline decoration-line-strong
        decoration-1 underline-offset-[3px] hover:decoration-current"
    >
      <span className="text-strong group-hover/guid:text-link">{text}</span>
    </Link>
  ) : (
    <span className="mono select-all whitespace-nowrap text-body">{text}</span>
  );

  return (
    <span className="group inline-flex shrink-0 items-center gap-1.5" title={value}>
      {body}
      <button
        type="button"
        aria-label="Copy"
        className="text-[10px] text-faint opacity-0 transition group-hover:opacity-100 hover:text-body"
        onClick={(event) => {
          event.stopPropagation();
          void navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 900);
        }}
      >
        {copied ? "copied" : "copy"}
      </button>
    </span>
  );
}

/* ── badges ──────────────────────────────────────────────────────────────────────────── */

// One pair per DS feedback color: the 500 as foreground, the 50 as fill.
const TONES = {
  good: "border-ok/30 bg-ok-bg text-ok",
  bad: "border-bad/30 bg-bad-bg text-bad",
  warn: "border-warn/30 bg-warn-bg text-warn",
  info: "border-info/30 bg-info-bg text-info",
  muted: "border-line-strong bg-elevated text-muted",
  violet: "border-alt/30 bg-alt-bg text-alt",
} as const;

export type Tone = keyof typeof TONES;

export function Badge({ children, tone = "muted" }: { children: React.ReactNode; tone?: Tone }) {
  return (
    <span
      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${TONES[tone]}`}
    >
      {children}
    </span>
  );
}

/**
 * The engine writes `applied`, `rejected` and `noop` (the CTMS path acking an event it could
 * not or need not apply); `overridden` is reserved for conflict management and will start
 * appearing without a UI change.
 */
export function OutcomeBadge({ outcome }: { outcome: string }) {
  const tone: Tone =
    outcome === "applied"
      ? "good"
      : outcome === "rejected"
        ? "bad"
        : outcome === "overridden"
          ? "warn"
          : "muted";
  return <Badge tone={tone}>{outcome}</Badge>;
}

/** `applied` only means drained; beside "all N rejected" it reads as the opposite verdict. */
export function StatusBadge({
  status,
  counts,
}: {
  status: string;
  counts?: Partial<Record<string, number>>;
}) {
  if (status === "applied" && allRejected(counts)) return null;
  const tone: Tone =
    status === "applied" || status === "sent"
      ? "good"
      : status === "failed"
        ? "bad"
        : status === "pending"
          ? "warn"
          : "muted";
  return <Badge tone={tone}>{status}</Badge>;
}

export function ProvenanceBadge({ provenance }: { provenance: string }) {
  const tone: Tone = provenance === "driver" ? "info" : provenance === "ctms" ? "violet" : "muted";
  return <Badge tone={tone}>{provenance}</Badge>;
}

type ReasonPath = "mobile" | "CTMS" | "both";

/**
 * Every reason code the engine journals today, with the ingress path that writes it. A code
 * missing here still renders — it just has no explanation until it is added.
 */
export const REASONS: Record<string, { path: ReasonPath; meaning: string; failure?: true }> = {
  invalid_envelope: {
    path: "both",
    failure: true,
    meaning: "The envelope did not parse; event type and often event guid are empty.",
  },
  unknown_event_type: { path: "both", failure: true, meaning: "No contract for this type." },
  unsupported_version: {
    path: "both",
    failure: true,
    meaning: "The contract has no processor for this schema version.",
  },
  invalid_payload: { path: "both", failure: true, meaning: "The payload failed the wire schema." },
  missing_entity_id: {
    path: "both",
    failure: true,
    meaning: "The payload names no usable entity guid.",
  },
  apply_error: {
    path: "both",
    failure: true,
    meaning: "The processor crashed; the batch or partition moved on.",
  },
  already_exists: { path: "both", meaning: "A create for an entity that is already stored." },
  not_assigned: { path: "mobile", meaning: "The load is not assigned to the sending driver." },
  not_found: { path: "mobile", meaning: "The vehicle or photo is missing or deleted." },
  wrong_step: { path: "mobile", meaning: "The photo belongs to the other leg." },
  wireframe: {
    path: "mobile",
    meaning: "The photo is a default wireframe slot and cannot be changed.",
  },
  superpay_owned: { path: "mobile", meaning: "SuperPay owns this load's paid flag." },
  already_paid: { path: "mobile", meaning: "Marked paid elsewhere first." },
  cannot_unpay: {
    path: "mobile",
    meaning: "The load cannot be marked unpaid (archived, unpaid or not delivered).",
  },
  stale: { path: "CTMS", meaning: "A newer CTMS fact for the entity is already applied." },
  no_change: {
    path: "CTMS",
    meaning: "The store already holds this fact; it still counts as the staleness watermark.",
  },
  entity_missing: {
    path: "CTMS",
    meaning: "The vehicle the event updates or removes is not mirrored.",
  },
  parent_missing: { path: "CTMS", meaning: "A child event arrived before its root." },
  root_missing: { path: "CTMS", meaning: "The root is not mirrored here." },
  driver_shadow_mode: {
    path: "CTMS",
    meaning: "The CTMS echo of a change the shadow-mode app already wrote.",
  },
};

/** A failure code is red whatever the outcome: CTMS journals its crashes and bad envelopes as `noop`. */
export function ReasonBadge({ code, outcome }: { code: string | null; outcome?: string }) {
  if (!code) return null;
  const reason = REASONS[code];
  const quiet = outcome === "noop" && !reason?.failure;
  return (
    <span title={reason ? `${reason.path}: ${reason.meaning}` : undefined}>
      <Badge tone={quiet ? "muted" : "bad"}>{code}</Badge>
    </span>
  );
}

export function EventType({ value }: { value: string | null }) {
  if (!value) {
    return (
      <span
        className="mono shrink-0 text-xs text-faint"
        title="The journal records the event type only for rows written after the column shipped, and never for an envelope it could not parse."
      >
        —
      </span>
    );
  }
  return <span className="mono shrink-0 text-xs text-strong">{value}</span>;
}

function allRejected(counts: Partial<Record<string, number>> | undefined) {
  const outcomes = Object.entries(counts ?? {}).filter(([, count]) => count);
  return outcomes.length === 1 && outcomes[0]![0] === "rejected";
}

/** A batch's `status` is only the drain state; this is what the apply decided for its events. */
export function OutcomeSummary({
  counts,
  loading,
  error,
  pending,
  partial,
}: {
  counts: Partial<Record<string, number>> | undefined;
  loading?: boolean;
  error?: Error | null;
  pending?: boolean;
  partial?: boolean;
}) {
  if (error) {
    return (
      <span className="text-xs text-bad" title={error.message}>
        journal unavailable
      </span>
    );
  }
  if (loading) return <span className="text-xs text-faint">…</span>;
  const entries = Object.entries(counts ?? {}).filter(([, count]) => count);
  if (entries.length === 0) {
    return (
      <span className="text-xs text-faint">{pending ? "not drained yet" : "no journal rows"}</span>
    );
  }
  const suffix = partial ? "+" : "";
  if (!pending && allRejected(counts)) {
    return (
      <Badge tone="bad">
        all {entries[0]![1]}
        {suffix} rejected
      </Badge>
    );
  }
  return (
    <span
      className="whitespace-nowrap text-xs text-muted"
      title={partial ? "Counted over the journal rows loaded so far." : undefined}
    >
      {entries.map(([outcome, count], index) => (
        <span key={outcome}>
          {index > 0 && " · "}
          <span className={outcome === "rejected" ? "text-bad" : undefined}>
            {count}
            {suffix} {outcome}
          </span>
        </span>
      ))}
      {pending && " so far"}
    </span>
  );
}

/* ── numbers ─────────────────────────────────────────────────────────────────────────── */

/**
 * The engine has several sequence numbers on different scales, and calling them all "seq"
 * is how an operator ends up comparing a journal id against a device cursor. Every number
 * here carries the column it came from in its title.
 */
export const SEQ = {
  id: "change_log.seq — the journal's internal id and insertion order. NOT the cursor scale, and never seen by a device",
  cursor:
    "change_log.conn_seq — the cursor this row consumed; 0 means it consumed none, which every rejection does",
  ceiling:
    "connection_cursors.last_seq — the newest cursor allocated on this connection. A pull freezes it and sends it back as `ceiling`",
  rootCursor: "feed_index.cursor — the cursor at which this load last changed",
  appliedTo:
    "applied_batches.applied_seq — the cursor this device has applied up to. Compare it with the connection's ceiling: equal means caught up, lower means the device is behind by the difference",
  publishOrder:
    "outbound_messages.seq — per-root publish order. A third scale, unrelated to the cursor",
  serverVersion: "server_version — the load's own version, backend-only; never sent to a device",
} as const;

export function Num({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | string;
  hint: string;
}) {
  return (
    <span className="inline-flex items-baseline gap-1" title={hint}>
      <span className="label">{label}</span>
      <span className="mono text-xs text-body">{value}</span>
    </span>
  );
}

/* ── time ────────────────────────────────────────────────────────────────────────────── */

export function Time({ value, relative = true }: { value: string | null; relative?: boolean }) {
  if (!value) return <span className="mono text-faint">—</span>;
  const date = new Date(value);
  return (
    <span className="mono text-muted" title={date.toISOString()}>
      {relative ? ago(date) : date.toLocaleString()}
    </span>
  );
}

function ago(date: Date): string {
  return `${duration(Date.now() - date.getTime())} ago`;
}

function duration(ms: number): string {
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
}

/**
 * A batch still `pending` long after it arrived.
 *
 * NOT an engine status — the engine only has pending / applied. The edge stores a batch and
 * enqueues its apply; if that enqueue was lost (broker down at receipt), the row waits for the
 * next trigger on the same root, because the relay that would re-fire it is not built yet. So
 * what this badge reports is the age of an un-drained row, and the grace period is this UI's
 * choice: a healthy drain finishes in well under a second.
 */
const DRAIN_GRACE_MS = 15 * 60_000;

export function StalePending({ batch }: { batch: { status: string; received_at: string } }) {
  if (batch.status !== "pending") return null;
  const age = Date.now() - new Date(batch.received_at).getTime();
  if (age < DRAIN_GRACE_MS) return null;

  return (
    <span
      title={
        "Accepted but never applied. Nothing will drain it until another batch arrives for the " +
        "same root — the engine has no relay for a lost apply trigger yet."
      }
    >
      <Badge tone="bad">not drained · {duration(age)}</Badge>
    </span>
  );
}

/* ── json ────────────────────────────────────────────────────────────────────────────── */

/** Event payloads are shown verbatim: what the device sent is the evidence. */
export function Json({ value, collapsed = false }: { value: unknown; collapsed?: boolean }) {
  const [open, setOpen] = useState(!collapsed);
  const text = JSON.stringify(value, null, 2);

  if (value === null || value === undefined || text === "{}") {
    return <span className="mono text-faint">empty</span>;
  }

  return (
    <div className="rounded-md border border-line bg-canvas">
      <div className="flex items-center justify-between border-b border-line px-2.5 py-1">
        <button type="button" className="label hover:text-strong" onClick={() => setOpen(!open)}>
          {open ? "▾ json" : "▸ json"}
        </button>
        <button
          type="button"
          className="label hover:text-strong"
          onClick={() => void navigator.clipboard.writeText(text)}
        >
          copy
        </button>
      </div>
      {open && (
        <pre className="mono max-h-96 overflow-auto px-2.5 py-2 leading-relaxed text-body">
          {text}
        </pre>
      )}
    </div>
  );
}

/**
 * A payload disclosure split in two: the toggle belongs in the row's line, the panel belongs
 * under it. Returning both keeps the open panel out of the flex line, which otherwise reflows
 * the whole row around it.
 */
export function useJsonDisclosure(value: unknown, label = "json") {
  const [open, setOpen] = useState(false);
  const text = JSON.stringify(value, null, 2);
  const empty = value === null || value === undefined || text === "{}";

  const toggle = empty ? (
    <span className="label shrink-0 text-faint">no {label}</span>
  ) : (
    <span className="inline-flex shrink-0 items-baseline gap-2">
      <button type="button" className="label hover:text-strong" onClick={() => setOpen(!open)}>
        {open ? "▾" : "▸"} {label}
      </button>
      {open && (
        <button
          type="button"
          className="label hover:text-strong"
          onClick={() => void navigator.clipboard.writeText(text)}
        >
          copy
        </button>
      )}
    </span>
  );

  const panel =
    open && !empty ? (
      <pre className="mono mt-2 max-h-72 overflow-auto rounded border border-line bg-canvas px-2.5 py-2 leading-relaxed text-body">
        {text}
      </pre>
    ) : null;

  return { toggle, panel };
}

/* ── layout helpers ──────────────────────────────────────────────────────────────────── */

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="label">{label}</div>
      <div className="mt-0.5 text-sm text-strong">{children}</div>
    </div>
  );
}

/**
 * Label over value, in columns that auto-fit.
 *
 * A guid needs ~290px and refuses to wrap, so a fixed column count squeezes it and the card
 * ends up scrolling sideways — which is not something a card should do. `auto-fit` gives each
 * field a column wide enough for a guid and drops to fewer columns instead.
 */
export function FieldGrid({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`grid gap-x-6 gap-y-3 p-4 ${className}`}
      style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 290px), 1fr))" }}
    >
      {children}
    </div>
  );
}

/**
 * A panel can be folded away, and stays folded across navigation and reloads — a support
 * engineer working one kind of ticket wants the same two panels open every time.
 *
 * `id` is the memory key: pass one whenever the title carries a count, or the key would change
 * under its own contents.
 */
export function Panel({
  title,
  id,
  actions,
  children,
  className = "",
}: {
  title: string;
  id?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  const [collapsed, setCollapsed] = usePersistedFlag(id ?? title);

  return (
    <section className={`panel ${className}`}>
      <header className="panel-head">
        <button
          type="button"
          className="group flex min-w-0 items-center gap-2"
          onClick={() => setCollapsed(!collapsed)}
          aria-expanded={!collapsed}
        >
          <Chevron collapsed={collapsed} />
          <h2 className="panel-title group-hover:text-strong">{title}</h2>
        </button>
        {actions}
      </header>
      {!collapsed && children}
    </section>
  );
}

function Chevron({ collapsed }: { collapsed: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`size-3 shrink-0 text-muted transition-transform group-hover:text-strong ${
        collapsed ? "-rotate-90" : ""
      }`}
      aria-hidden
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

const COLLAPSE_KEY = "sync-inspector:collapsed";

function readCollapsed(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(COLLAPSE_KEY) ?? "{}") as Record<string, boolean>;
  } catch {
    return {};
  }
}

/** A remembered boolean. One localStorage object holds every collapse state in the console. */
export function usePersistedFlag(key: string): [boolean, (value: boolean) => void] {
  const [collapsed, setState] = useState(() => readCollapsed()[key] ?? false);

  function set(value: boolean) {
    setState(value);
    const all = { ...readCollapsed(), [key]: value };
    try {
      localStorage.setItem(COLLAPSE_KEY, JSON.stringify(all));
    } catch {
      // A console in a locked-down profile still has to work; it just forgets.
    }
  }

  return [collapsed, set];
}

/* ── states ──────────────────────────────────────────────────────────────────────────── */

export function Spinner({ label = "loading" }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-8 text-sm text-muted">
      <span className="size-3 animate-spin rounded-full border-2 border-line-strong border-t-link" />
      {label}
    </div>
  );
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <div className="px-4 py-8 text-center text-sm text-muted">{children}</div>;
}

export function ErrorBox({ error }: { error: unknown }) {
  const api = error instanceof ApiError ? error : null;
  const title = api?.isNotFound
    ? "Not found"
    : api?.isAuth
      ? "Not authorized"
      : api
        ? `Request failed (${api.status})`
        : "Request failed";
  const detail = api?.detail ?? (error instanceof Error ? error.message : String(error));
  const hint =
    api?.status === 403
      ? "This account is outside the domains the engine allows."
      : api?.isAuth
        ? "Reload to renew the Cloudflare Access session."
        : null;

  return (
    <div className="m-4 rounded-md border border-bad/40 bg-bad-bg p-4">
      <div className="text-sm font-semibold text-bad">{title}</div>
      <p className="mt-1 text-sm text-body">{detail}</p>
      {hint && <p className="mt-2 text-xs text-muted">{hint}</p>}
    </div>
  );
}
